// Los préstamos que se descuentan de a poco.
//
// ── Lo que pidió el cliente ──
//
// «Le presté un millón a la finca, pero esa persona no va a pagar todo en la
// quincena. Quiero descontarle 200 semanal.»
//
// Hasta hoy un préstamo era TODO O NADA: al pagarle la leche se elegía cuáles
// descontar y cada uno se iba completo. Un millón de golpe contra una quincena
// de leche deja a la finca sin cobrar nada, y por eso nadie lo usaba así: se
// llevaba en un cuaderno aparte, que es donde se pierden.
//
// ── El saldo NO se guarda ──
//
// Sale de restarle los abonos al monto. Un saldo guardado y una lista de abonos
// terminan diciendo cosas distintas —basta un abono que se corrija después— y
// entonces no se sabe cuál creer. La lista es la verdad; el saldo, una resta.

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

// Cuánto le queda debiendo a este préstamo.
// ── DE CUÁL PRÉSTAMO ES ESTE ABONO ───────────────────────────────────────
//
// Los abonos de una finca se enlazan por `prestamo_id` y los de un trabajador
// por `vale_id`. Mirando solo el primero, la pantalla de nómina enseñaba el
// vale ENTERO como pendiente aunque el trabajador llevara medio pagado — y el
// panel de sus vales no le mostraba ni un abono.
//
// La plata nunca se cobró de más: el servidor calcula el saldo por su cuenta y
// topa el descuento. Lo que mentía era el papel de la pantalla, que es lo que
// mira quien decide cuánto descontar.
const esDe = (abono, prestamo) =>
  String(abono.prestamo_id ?? abono.vale_id) === String(prestamo.id);

export function saldoDe(prestamo, abonos = []) {
  const monto = Math.max(0, num(prestamo && prestamo.monto));
  const abonado = (Array.isArray(abonos) ? abonos : [])
    .filter(a => esDe(a, prestamo))
    .reduce((s, a) => s + Math.max(0, num(a.monto)), 0);
  return Math.max(0, Math.round(monto - abonado));
}

// Lo abonado a este préstamo, con sus fechas. Es lo que se le enseña al dueño
// de la finca cuando pregunta —«¿y lo que le he abonado?»— y sin eso el
// descuento es la palabra de uno contra la del otro.
export function abonosDe(prestamo, abonos = []) {
  return (Array.isArray(abonos) ? abonos : [])
    .filter(a => esDe(a, prestamo))
    .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));
}

// Lo que el programa PROPONE descontar en este pago.
//
// Tres topes, y ninguno se puede saltar:
//
//   1. la CUOTA pactada —los $200.000 semanales—, o el saldo entero si no se
//      pactó cuota, que es como funcionaba antes;
//   2. lo que queda debiendo, para no cobrar de más;
//   3. LO QUE SE LE ESTÁ PAGANDO. Este es el que importa: la semana que la
//      finca entregó poca leche, descontarle la cuota completa dejaría el neto
//      en negativo — o sea, la finca saliendo a deber por haber entregado
//      leche. Eso no puede pasar nunca.
//
// Del más viejo al más nuevo: es el orden en que se salda cualquier cuenta y el
// que la persona espera.
//
// PROPONE, no impone. Quien paga puede bajarlo o saltarlo, porque ese trato se
// negocia en la puerta y el programa no estaba ahí.
// `sinCuotaSeMarca` distingue los DOS negocios que usan esta función.
//
//   · PRÉSTAMO A UNA FINCA (false, lo normal). Sin cuota no se marca solo:
//     nadie lo autorizó. Es la regla que se decidió por el caso de TORIBIO
//     ANDRADE, a quien la hoja llevaba siete meses anunciando un descuento de
//     $3.000.000 que nunca se hacía.
//
//   · VALE DE UN TRABAJADOR (true). Ahí sí se marca solo, y es otra cosa: el
//     vale es un adelanto CONTRA EL SUELDO QUE SE ESTÁ LIQUIDANDO en esa misma
//     pantalla. Descontarlo es lo esperado, y su rótulo lo dice desde siempre:
//     «desmarca los que quieras dejar para la próxima».
//
// Se pasa por fuera y no se adivina aquí: la función no puede saber si lo que
// le mandaron son préstamos o vales, y adivinarlo por el nombre de un campo es
// cómo se cuelan los errores que nadie vuelve a encontrar.
export function proponerDescuentos(prestamos, abonos, disponible,
                                   { sinCuotaSeMarca = false } = {}) {
  let queda = Math.max(0, Math.round(num(disponible)));

  return (Array.isArray(prestamos) ? prestamos : [])
    .map(p => ({ p, saldo: saldoDe(p, abonos) }))
    .filter(x => x.saldo > 0)
    .sort((a, b) => (new Date(a.p.fecha || 0) - new Date(b.p.fecha || 0))
                 || (num(a.p.id) - num(b.p.id)))
    .map(({ p, saldo }) => {
      // ── SIN CUOTA NO SE DESCUENTA SOLO ───────────────────────────────────
      //
      // Antes, un préstamo sin cuota se marcaba solo por el saldo ENTERO. A
      // TORIBIO ANDRADE, con un préstamo de $3.000.000 del 15 de febrero, la
      // hoja llevaba siete meses anunciando que se le descontaban los tres
      // millones — y nunca se le descontaron, porque Gabriel decía que no cada
      // quincena. El papel decía una cosa y la caja hacía otra.
      //
      // La cuota es la única forma de decirle al programa «descuéntele tanto
      // cada vez». Sin ella, nadie lo ha autorizado: el préstamo se enseña,
      // pero la casilla arranca en blanco y lo mueve quien paga.
      const cuota = num(p.cuota) > 0 ? Math.round(num(p.cuota)) : null;
      const propuesto = cuota ? Math.min(cuota, saldo, queda)
                        : (sinCuotaSeMarca ? Math.min(saldo, queda) : 0);
      // Y lo que se pone al marcar la casilla A MANO: todo lo que alcance.
      //
      // Va aparte de `propuesto` porque son dos preguntas distintas: qué se
      // descuenta sin que nadie diga nada —solo la cuota— y cuánto ofrecerle a
      // quien decide descontarlo hoy. Sin esto, marcar un préstamo sin cuota
      // pondría cero y la casilla no haría nada.
      const alMarcar = Math.min(cuota || saldo, saldo, queda);
      queda -= propuesto;
      return {
        id: p.id,
        prestamo: p,
        saldo,
        cuota,
        propuesto,
        alMarcar,
        // Para poder decirlo en pantalla en vez de que el número aparezca
        // recortado sin explicación: «se pactaron 200.000 y solo alcanzó para
        // 130.000» es una frase que quien paga tiene que poder leer.
        //
        // Solo con cuota: sin ella no hay nada pactado que se haya quedado
        // corto. `Math.min(null, saldo)` daría cero y la comparación mentiría.
        recortadoPorElPago: !!cuota && propuesto < Math.min(cuota, saldo),
        // Con este descuento el préstamo queda saldado.
        salda: propuesto >= saldo && saldo > 0,
      };
    });
}

// Lo que se le entrega en la mano.
export function netoDelPago(bruto, descuentos = []) {
  const total = (Array.isArray(descuentos) ? descuentos : [])
    .reduce((s, d) => s + Math.max(0, num(d.propuesto ?? d.monto)), 0);
  const b = Math.max(0, Math.round(num(bruto)));
  return { bruto: b, descuento: Math.min(total, b), neto: Math.max(0, b - Math.min(total, b)) };
}

// Cuánto le queda debiendo la finca en total, para el renglón de arriba.
export function deudaTotal(prestamos, abonos) {
  return (Array.isArray(prestamos) ? prestamos : [])
    .reduce((s, p) => s + saldoDe(p, abonos), 0);
}

// En cuántas cuotas se acaba de pagar, al ritmo pactado. Es la pregunta que
// hace el dueño de la finca al recibir el préstamo: «¿y hasta cuándo me
// descuentan?». Sin cuota pactada no hay plazo que calcular.
export function cuantasFaltan(prestamo, abonos) {
  const saldo = saldoDe(prestamo, abonos);
  const cuota = num(prestamo && prestamo.cuota);
  if (saldo <= 0) return 0;
  if (cuota <= 0) return null;
  return Math.ceil(saldo / cuota);
}

// ── EL RENGLÓN QUE EXPLICA UN PRÉSTAMO EN UN COMPROBANTE ──────────────────
//
// El comprobante decía solo «Menos préstamo del 6 de sept: −$100.000».
// Correcto, y sin embargo incompleto: no dice de cuánto era el préstamo, ni
// cuánto lleva abonado, ni cuánto le queda.
//
// A la finca no le duele tanto —su relación sí lo explica desde ayer— pero el
// TRABAJADOR no tiene otra hoja. Ve que le descontaron cien mil y ya: para
// saber cuánto le falta tiene que preguntar. Y es a quien más le importa, que
// se lo descuentan de su semana y no de una quincena de millones.
//
// Va aquí, en una sola función, y la usan los dos comprobantes. La misma frase
// escrita en dos pantallas se desincroniza el día que se toca una — ya pasó
// hoy con la lista de frecuencias.
//
// `abonos` son los de ANTES de este pago: el abono de hoy todavía no está
// guardado cuando se arma el papel, y por eso se pasa aparte.
export function detalleDelPrestamo(prestamo, abonos, descontadoHoy) {
  const monto = Math.max(0, num(prestamo && prestamo.monto));
  const hoy = Math.max(0, num(descontadoHoy));
  const antes = (Array.isArray(abonos) ? abonos : [])
    .filter(a => esDe(a, prestamo))
    .reduce((s, a) => s + Math.max(0, num(a.monto)), 0);

  const abonado = Math.round(antes + hoy);
  const queda = Math.max(0, Math.round(monto - abonado));

  return {
    monto: Math.round(monto),
    abonado,
    queda,
    // Si con este pago se salda, se dice con todas las letras: es la frase que
    // el trabajador espera oír y la que evita que siga preguntando.
    saldado: queda === 0,
  };
}
